import { createSlice } from '@reduxjs/toolkit'

const initialState = {
    // Battery configuration
    battery: null,
    batteryQty: 1,
    batteryOnly: false,
    confirmBatteryOnly: false,
    batteryOnlyInverterType: 'string', // 'string' or 'hybrid'

    // Inverter configuration
    requiredInverters: [],
    tempInverter: [{
        from: 0,
        to: 40000,
        inverter: { id: -2, description: 'No Inverter Required', manufacturer: '' },
        id: -2,
        hybrid: true,
        is3phase: false,
        offgrid: false,
        overwrite: true
    }],
    tempSelectedInverter: -2,
    is3Phase: false,
    offgrid: false,
    hybrid: false,
    standardInverters: true,

    // AC Controller (for battery-only systems)
    acController: {},

    // Panel arrays
    panelArrays: [],

    // Savings projections
    savings: []
}

const systemSlice = createSlice({
    name: 'system',
    initialState,
    reducers: {
        // Battery actions
        setBattery: (state, action) => {
            state.battery = action.payload
        },
        setBatteryQty: (state, action) => {
            state.batteryQty = action.payload
        },
        setBatteryOnly: (state, action) => {
            state.batteryOnly = action.payload
        },
        setConfirmBatteryOnly: (state, action) => {
            state.confirmBatteryOnly = action.payload
        },
        setBatteryOnlyInverterType: (state, action) => {
            state.batteryOnlyInverterType = action.payload
        },

        // Inverter actions
        setRequiredInverters: (state, action) => {
            state.requiredInverters = action.payload
        },
        addInverter: (state, action) => {
            state.requiredInverters.push(action.payload)
        },
        updateInverter: (state, action) => {
            const { index, data } = action.payload
            if (index >= 0 && index < state.requiredInverters.length) {
                state.requiredInverters[index] = { ...state.requiredInverters[index], ...data }
            }
        },
        removeInverter: (state, action) => {
            state.requiredInverters.splice(action.payload, 1)
        },
        setTempInverter: (state, action) => {
            state.tempInverter = action.payload
        },
        setTempSelectedInverter: (state, action) => {
            state.tempSelectedInverter = action.payload
        },
        setIs3Phase: (state, action) => {
            state.is3Phase = action.payload
        },
        setOffgrid: (state, action) => {
            state.offgrid = action.payload
        },
        setHybrid: (state, action) => {
            state.hybrid = action.payload
        },
        setStandardInverters: (state, action) => {
            state.standardInverters = action.payload
        },

        // AC Controller
        setACController: (state, action) => {
            state.acController = action.payload
        },

        // Panel arrays
        setPanelArrays: (state, action) => {
            state.panelArrays = action.payload
        },
        addPanelArray: (state, action) => {
            state.panelArrays.push(action.payload)
        },
        updatePanelArray: (state, action) => {
            const { index, data } = action.payload
            if (index >= 0 && index < state.panelArrays.length) {
                state.panelArrays[index] = { ...state.panelArrays[index], ...data }
            }
        },
        removePanelArray: (state, action) => {
            state.panelArrays.splice(action.payload, 1)
        },

        // Savings
        setSavings: (state, action) => {
            state.savings = action.payload
        },

        // Bulk update from quote data
        updateSystemFromQuote: (state, action) => {
            const data = action.payload
            if (data.battery !== undefined) state.battery = data.battery
            if (data.batteryQty !== undefined) state.batteryQty = data.batteryQty
            if (data.batteryOnly !== undefined) state.batteryOnly = data.batteryOnly
            if (data.batteryonly !== undefined) state.batteryOnly = data.batteryonly
            if (data.confirmBatteryOnly !== undefined) state.confirmBatteryOnly = data.confirmBatteryOnly
            if (data.confirm_batteryonly !== undefined) state.confirmBatteryOnly = data.confirm_batteryonly
            if (data.batteryOnlyInverterType !== undefined) state.batteryOnlyInverterType = data.batteryOnlyInverterType
            if (data.batteryonly_inv !== undefined) state.batteryOnlyInverterType = data.batteryonly_inv
            if (data.requiredInverters !== undefined) state.requiredInverters = data.requiredInverters
            if (data.required_inverters !== undefined) state.requiredInverters = data.required_inverters
            if (data.is3Phase !== undefined) state.is3Phase = data.is3Phase
            if (data.is3phase !== undefined) state.is3Phase = data.is3phase
            if (data.offgrid !== undefined) state.offgrid = data.offgrid
            if (data.hybrid !== undefined) state.hybrid = data.hybrid
            if (data.standardInverters !== undefined) state.standardInverters = data.standardInverters
            if (data.standardinverters !== undefined) state.standardInverters = data.standardinverters
            if (data.acController !== undefined) state.acController = data.acController
            if (data.accontroller !== undefined) state.acController = data.accontroller
            if (data.panelArrays !== undefined) state.panelArrays = data.panelArrays
            if (data.savings !== undefined) state.savings = data.savings
        },

        resetSystem: () => initialState
    }
})

export const {
    setBattery,
    setBatteryQty,
    setBatteryOnly,
    setConfirmBatteryOnly,
    setBatteryOnlyInverterType,
    setRequiredInverters,
    addInverter,
    updateInverter,
    removeInverter,
    setTempInverter,
    setTempSelectedInverter,
    setIs3Phase,
    setOffgrid,
    setHybrid,
    setStandardInverters,
    setACController,
    setPanelArrays,
    addPanelArray,
    updatePanelArray,
    removePanelArray,
    setSavings,
    updateSystemFromQuote,
    resetSystem
} = systemSlice.actions

// Selectors
export const selectBattery = (state) => state.system.battery
export const selectBatteryQty = (state) => state.system.batteryQty
export const selectBatteryOnly = (state) => state.system.batteryOnly
export const selectConfirmBatteryOnly = (state) => state.system.confirmBatteryOnly
export const selectBatteryOnlyInverterType = (state) => state.system.batteryOnlyInverterType
export const selectRequiredInverters = (state) => state.system.requiredInverters
export const selectTempInverter = (state) => state.system.tempInverter
export const selectTempSelectedInverter = (state) => state.system.tempSelectedInverter
export const selectIs3Phase = (state) => state.system.is3Phase
export const selectOffgrid = (state) => state.system.offgrid
export const selectHybrid = (state) => state.system.hybrid
export const selectStandardInverters = (state) => state.system.standardInverters
export const selectACController = (state) => state.system.acController
export const selectPanelArrays = (state) => state.system.panelArrays
export const selectSavings = (state) => state.system.savings

// Computed selectors
export const selectTotalPanelWattage = (state) => {
    return state.system.panelArrays.reduce((total, array) => {
        return total + (array.panels || 0) * (array.wattage || 0)
    }, 0)
}

export const selectTotalInverterWattage = (state) => {
    return state.system.requiredInverters.reduce((total, inv) => {
        return total + (inv.watts || 0)
    }, 0)
}

export const selectBatteryCapacity = (state) => {
    if (!state.system.battery) return 0
    return (state.system.battery.capacity || 0) * state.system.batteryQty
}

export default systemSlice.reducer
